/**
 * StructuredAgentEditor — single editor that handles BOTH:
 *
 *   • Library / platform scope (no tid+sid)  →  /library/{env}/agent-defs/:agentId
 *     Reads/writes via api.getPlatformAgentDef + api.writePlatformAgentDef
 *
 *   • Sub-tenant scope (with tid+sid)        →  /tenants/:tid/sub-tenants/:sid/{env}/agent-defs/:agentId
 *     Reads/writes via api.getSubTenantAgentDef + api.writeSubTenantAgentDef
 *
 * The page detects which mode it's in from the URL params. Same UI for both.
 *
 * Layout (mirrors SubTenantPromptEdit / LibrarySpecialtyAgent):
 *   PageHeader (Version + Status badges)
 *   Tabs: Configuration | Persona | Rules | Steps | Exit | Tools | Raw JSON | History
 *   Configuration tab — read-only summary (id, name, role, direction)
 *   Persona / Rules / Steps / Exit / Tools — sectioned editors
 *   Raw JSON — fallback escape hatch (full JSON edit with validation)
 *   History — version timeline
 */

import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import * as api from "../api.js";
import {
  PageHeader,
  Loading,
  ErrorBanner,
  VersionHistory,
  Tabs,
  StatusBadge,
  VersionBadge,
} from "../components.jsx";
import { AuthoredView, CompiledView } from "../components/prompt-builder.jsx";
import { useLibraryEnv } from "../LibraryEnvContext.jsx";

// ─── Field building blocks ─────────────────────────────────────────

function FieldLabel({ children, hint }) {
  return (
    <div style={{ marginBottom: 6 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: "#444" }}>{children}</label>
      {hint && <div style={{ fontSize: 11, color: "#999", marginTop: 2 }}>{hint}</div>}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, readOnly, multiline = false, rows = 3 }) {
  if (multiline) {
    return (
      <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        readOnly={readOnly}
        placeholder={placeholder}
        rows={rows}
        style={{
          width: "100%", padding: 8, fontSize: 13, border: "1px solid #ccc",
          borderRadius: 4, resize: "vertical", fontFamily: "inherit",
          background: readOnly ? "#f6f7f9" : "#fff",
        }}
      />
    );
  }
  return (
    <input
      type="text"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      readOnly={readOnly}
      placeholder={placeholder}
      style={{
        width: "100%", padding: "6px 8px", fontSize: 13, border: "1px solid #ccc",
        borderRadius: 4, background: readOnly ? "#f6f7f9" : "#fff",
      }}
    />
  );
}

function ChipsInput({ values, onChange, readOnly, placeholder = "Add..." }) {
  const [input, setInput] = useState("");
  const list = Array.isArray(values) ? values : [];
  const add = () => {
    const v = input.trim();
    if (!v) return;
    if (list.includes(v)) { setInput(""); return; }
    onChange([...list, v]);
    setInput("");
  };
  const remove = (v) => onChange(list.filter((x) => x !== v));
  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 6 }}>
        {list.length === 0 && <span style={{ fontSize: 12, color: "#999" }}>(none)</span>}
        {list.map((v) => (
          <span
            key={v}
            style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              padding: "3px 8px", background: "#eef3fa", border: "1px solid #cfd8e3",
              borderRadius: 12, fontSize: 12, color: "#2E4F72",
            }}
          >
            {v}
            {!readOnly && (
              <button
                type="button"
                onClick={() => remove(v)}
                style={{
                  background: "transparent", border: "none", cursor: "pointer",
                  color: "#888", fontSize: 14, padding: 0, lineHeight: 1,
                }}
              >×</button>
            )}
          </span>
        ))}
      </div>
      {!readOnly && (
        <div style={{ display: "flex", gap: 6 }}>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
            placeholder={placeholder}
            style={{ flex: 1, padding: "5px 8px", fontSize: 12, border: "1px solid #ccc", borderRadius: 4 }}
          />
          <button type="button" className="btn sm" onClick={add}>Add</button>
        </div>
      )}
    </div>
  );
}

// ─── Main editor ────────────────────────────────────────────────────

export default function StructuredAgentEditor() {
  const params = useParams();
  const { env, isLive } = useLibraryEnv();
  const isSubTenant = !!(params.tid && params.sid);
  const tid = params.tid;
  const sid = params.sid;
  // Route may use either :agentId or :id (the legacy /library/agents/:id alias)
  const agentId = params.agentId || params.id;

  const [tab, setTab] = useState("configuration");
  const [doc, setDoc] = useState(null);
  const [definition, setDefinition] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [rawJson, setRawJson] = useState("");
  const [rawJsonError, setRawJsonError] = useState(null);

  const apiGet = useCallback(() => {
    return isSubTenant
      ? api.getSubTenantAgentDef(tid, sid, agentId, env)
      : api.getPlatformAgentDef(agentId, env);
  }, [isSubTenant, tid, sid, agentId, env]);

  const apiWrite = useCallback((body) => {
    return isSubTenant
      ? api.writeSubTenantAgentDef(tid, sid, agentId, body)
      : api.writePlatformAgentDef(agentId, body);
  }, [isSubTenant, tid, sid, agentId]);

  const apiHistory = useCallback(() => {
    return isSubTenant
      ? api.getSubTenantAgentDefHistory(tid, sid, agentId)
      : api.getPlatformAgentDefHistory(agentId);
  }, [isSubTenant, tid, sid, agentId]);

  const apiCompile = useCallback((direction) => {
    return isSubTenant
      ? api.getSubTenantAgentDefCompiledPrompt(tid, sid, agentId, direction)
      : api.getPlatformAgentDefCompiledPrompt(agentId, direction);
  }, [isSubTenant, tid, sid, agentId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await apiGet();
      setDoc(r.data);
      const def = r.data?.definition || JSON.parse(r.data?.content || "{}");
      setDefinition(def);
      setRawJson(JSON.stringify(def, null, 2));
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [apiGet]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (tab !== "history") return;
    let cancelled = false;
    setHistoryLoading(true);
    (async () => {
      try {
        const r = await apiHistory();
        if (!cancelled) setHistory(r.data || []);
      } catch {
        if (!cancelled) setHistory([]);
      } finally {
        if (!cancelled) setHistoryLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tab, apiHistory]);

  const updateField = (path, value) => {
    setDefinition((prev) => {
      const next = JSON.parse(JSON.stringify(prev || {}));
      let cursor = next;
      for (let i = 0; i < path.length - 1; i++) {
        if (cursor[path[i]] == null) cursor[path[i]] = {};
        cursor = cursor[path[i]];
      }
      cursor[path[path.length - 1]] = value;
      return next;
    });
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await apiWrite({
        definition,
        author: "admin",
        commit_msg: `Edit structured definition for ${agentId}`,
      });
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const saveRaw = async () => {
    setRawJsonError(null);
    let parsed;
    try { parsed = JSON.parse(rawJson); }
    catch (err) { setRawJsonError(`Invalid JSON: ${err.message}`); return; }
    setBusy(true);
    try {
      await apiWrite({
        definition: parsed,
        author: "admin",
        commit_msg: `Edit structured definition for ${agentId} (raw JSON)`,
      });
      await load();
      setTab("configuration");
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Loading />;
  if (!definition) return <div className="empty-state"><h3>Not found</h3></div>;

  const breadcrumbs = isSubTenant
    ? [
        { label: "Tenants", to: "/tenants" },
        { label: tid, to: `/tenants/${tid}` },
        { label: sid, to: `/tenants/${tid}/sub-tenants/${sid}/${env}` },
        { label: "Built-in Agents" },
        { label: agentId },
      ]
    : [
        { label: "Library", to: `/library/${env}/global-rules` },
        { label: "Built-in Agents", to: `/library/${env}/built-in-agents` },
        { label: agentId },
      ];

  return (
    <>
      <PageHeader
        title={definition.name || agentId}
        subtitle={
          isSubTenant
            ? `Sub-tenant override of platform agent definition — ${agentId}`
            : `Platform structured agent definition — ${agentId}`
        }
        breadcrumbs={breadcrumbs}
        actions={
          <>
            <VersionBadge version={doc?.version} />
            <StatusBadge doc={doc} />
            {!isLive && (
              <button className="btn primary sm" onClick={save} disabled={busy}>
                {busy ? "Saving..." : "Save Draft"}
              </button>
            )}
          </>
        }
      />

      <Tabs
        tabs={[
          { id: "configuration", label: "Configuration" },
          { id: "groups", label: "Agent Group" },
          { id: "prompt", label: "Prompt" },
          { id: "protocols", label: `Agent Rules (${(definition.linked_protocols || []).length})` },
          { id: "history", label: `History${history.length ? ` (${history.length})` : ""}` },
        ]}
        active={tab}
        onChange={setTab}
      />

      <div className="tab-content">
        <ErrorBanner error={error} />

        {tab === "configuration" && (
          <ConfigurationTab definition={definition} updateField={updateField} readOnly={isLive} agentId={agentId} />
        )}
        {tab === "groups" && (
          <AgentGroupTab definition={definition} updateField={updateField} readOnly={isLive} isSubTenant={isSubTenant} />
        )}
        {tab === "prompt" && (
          <PromptTabWithSubtabs
            definition={definition}
            updateField={updateField}
            readOnly={isLive}
            rawJson={rawJson}
            setRawJson={setRawJson}
            rawJsonError={rawJsonError}
            setRawJsonError={setRawJsonError}
            saveRaw={saveRaw}
            busy={busy}
            apiCompile={apiCompile}
          />
        )}
        {tab === "protocols" && (
          <ProtocolsTab definition={definition} updateField={updateField} readOnly={isLive} />
        )}
        {tab === "history" && (
          <div className="card">{historyLoading ? <Loading /> : <VersionHistory versions={history} />}</div>
        )}
      </div>
    </>
  );
}

// ─── Prompt tab with structured subtabs ───────────────────────────
//
// Top-level Prompt tab. Subtabs let you edit individual structured
// sections (Persona / Rules / Steps / Exit / Tools), see the compiled
// preview, or drop into raw JSON for anything the form doesn't expose.
export function PromptTabWithSubtabs({ definition, updateField, readOnly, rawJson, setRawJson, rawJsonError, setRawJsonError, saveRaw, busy, apiCompile }) {
  const [sub, setSub] = useState("persona");
  const [direction, setDirection] = useState(
    Array.isArray(definition?.entry?.direction) && definition.entry.direction.includes("outbound") && !definition.entry.direction.includes("inbound")
      ? "outbound" : "inbound"
  );
  const [compiled, setCompiled] = useState(null);
  const [compiledLoading, setCompiledLoading] = useState(false);
  const [compiledError, setCompiledError] = useState(null);

  // Lazy-fetch the compiled prompt when the user opens Combined / Master.
  useEffect(() => {
    if (sub !== "combined" && sub !== "master") return;
    if (!apiCompile) return;
    let cancelled = false;
    setCompiledLoading(true);
    setCompiledError(null);
    (async () => {
      try {
        const r = await apiCompile(direction);
        if (!cancelled) setCompiled(r.data);
      } catch (err) {
        if (!cancelled) { setCompiledError(err); setCompiled(null); }
      } finally {
        if (!cancelled) setCompiledLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sub, direction, apiCompile]);

  const subtabs = [
    { id: "persona", label: "Persona" },
    { id: "rules", label: `Rules (${(definition.rules || []).length})` },
    { id: "steps", label: `Steps (${(definition.steps || []).length})` },
    { id: "exit", label: "Exit" },
    { id: "tools", label: `Tools (${(definition.tools || []).length})` },
    { id: "preview", label: "Preview" },
    { id: "combined", label: "Combined (agent only)" },
    { id: "master", label: "Master Combined (LLM view)" },
    { id: "raw", label: "Raw JSON" },
  ];

  return (
    <div>
      <div style={{
        display: "flex", gap: 6, padding: "8px 0",
        borderBottom: "1px solid #e5e5e5", marginBottom: 16, flexWrap: "wrap",
      }}>
        {subtabs.map((t) => (
          <button
            key={t.id}
            className="btn btn-sm"
            onClick={() => setSub(t.id)}
            style={sub === t.id ? { background: "#2E4F72", color: "#fff" } : {}}
          >
            {t.label}
          </button>
        ))}
      </div>

      {sub === "persona" && (
        <PersonaTab definition={definition} updateField={updateField} readOnly={readOnly} />
      )}
      {sub === "rules" && (
        <RulesTab definition={definition} updateField={updateField} readOnly={readOnly} />
      )}
      {sub === "steps" && (
        <StepsTab definition={definition} updateField={updateField} readOnly={readOnly} />
      )}
      {sub === "exit" && (
        <ExitTab definition={definition} updateField={updateField} readOnly={readOnly} />
      )}
      {sub === "tools" && (
        <ToolsTab definition={definition} updateField={updateField} readOnly={readOnly} />
      )}
      {sub === "preview" && (
        <div className="card"><AuthoredView definition={definition} /></div>
      )}
      {(sub === "combined" || sub === "master") && (
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <span className="muted small">Direction:</span>
            <button
              className="btn btn-sm"
              onClick={() => setDirection("inbound")}
              style={direction === "inbound" ? { background: "#2E4F72", color: "#fff" } : {}}
            >Inbound</button>
            <button
              className="btn btn-sm"
              onClick={() => setDirection("outbound")}
              style={direction === "outbound" ? { background: "#2E4F72", color: "#fff" } : {}}
            >Outbound</button>
            <span className="muted small" style={{ marginLeft: 8 }}>
              {sub === "combined"
                ? "This agent's content only — no framework rules"
                : "Universal + framework + this agent — what the LLM actually receives"}
            </span>
          </div>
          {compiledLoading && <Loading />}
          {compiledError && <ErrorBanner error={compiledError} />}
          {!compiledLoading && !compiledError && compiled && compiled.source === "prose" && (
            <div style={{
              padding: 14, background: "#fff8e1", border: "1px solid #ffe0a3",
              borderRadius: 6, fontSize: 13, color: "#5b4400",
            }}>
              <strong>No structured definition available.</strong>
              <div style={{ marginTop: 6 }}>{compiled.message}</div>
            </div>
          )}
          {!compiledLoading && !compiledError && compiled && compiled.source === "structured" && sub === "combined" && (
            <CompiledView
              prompt={compiled.prompt_agent_only}
              ruleMap={compiled.rule_map_agent_only}
              title="Agent contribution only (no framework rules)"
            />
          )}
          {!compiledLoading && !compiledError && compiled && compiled.source === "structured" && sub === "master" && (
            <CompiledView
              prompt={compiled.prompt}
              ruleMap={compiled.rule_map}
              title="Full prompt sent to LLM (universal + framework + agent)"
            />
          )}
        </div>
      )}
      {sub === "raw" && (
        <div className="card">
          <FieldLabel hint="Edit the entire structured definition as JSON. Use this when you need to change something the form doesn't expose. Validation runs on Save.">
            Raw JSON ({rawJson.length.toLocaleString()} chars)
          </FieldLabel>
          <textarea
            value={rawJson}
            onChange={(e) => { setRawJson(e.target.value); setRawJsonError(null); }}
            readOnly={readOnly}
            spellCheck="false"
            style={{
              width: "100%", minHeight: 500, padding: 12,
              fontFamily: "var(--font-mono)", fontSize: 12, lineHeight: 1.55,
              border: rawJsonError ? "1px solid #d32f2f" : "1px solid #ccc",
              borderRadius: 4, resize: "vertical", background: readOnly ? "#f6f7f9" : "#fafafa",
            }}
          />
          {rawJsonError && (
            <div style={{ color: "#d32f2f", fontSize: 12, marginTop: 6 }}>{rawJsonError}</div>
          )}
          {!readOnly && (
            <div style={{ marginTop: 12 }}>
              <button className="btn primary sm" onClick={saveRaw} disabled={busy}>
                {busy ? "Saving..." : "Save Raw JSON"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Agent Group tab ──────────────────────────────────────────────
function AgentGroupTab({ definition, updateField, readOnly, isSubTenant }) {
  const specialties = definition.specialties || [];
  return (
    <div className="card">
      <div className="card-header">
        <h3>Agent Group</h3>
        <span className="muted small" style={{ marginLeft: 8 }}>
          Which specialty groups this agent belongs to. Built-in agents typically have no specialty (universal).
        </span>
      </div>
      <div className="form-field">
        <FieldLabel hint='Add specialty IDs like "oncology" or "orthopedics" to scope this agent.'>Specialties</FieldLabel>
        <ChipsInput
          values={specialties}
          onChange={(v) => updateField(["specialties"], v)}
          readOnly={readOnly}
          placeholder='e.g. oncology'
        />
      </div>
      {isSubTenant && (
        <div className="form-field" style={{ marginTop: 16, padding: 12, background: "#eef3fa", border: "1px solid #cfd8e3", borderRadius: 6, fontSize: 12, color: "#2E4F72" }}>
          <strong>Sub-tenant override.</strong> This is a per-sub-tenant fork of the platform agent definition.
          Specialties typically inherit from the platform default; only set them here if you need a per-tenant override.
        </div>
      )}
    </div>
  );
}

// ─── Agent Rules (linked protocols) tab ───────────────────────────
function ProtocolsTab({ definition, updateField, readOnly }) {
  const linked = definition.linked_protocols || [];
  return (
    <div className="card">
      <div className="card-header">
        <h3>Agent Rules — Linked Protocols ({linked.length})</h3>
        <span className="muted small" style={{ marginLeft: 8 }}>Protocols this agent consults at runtime.</span>
      </div>
      <FieldLabel hint='Each protocol is { category, protocol_id }. Edit as JSON for now — full protocol picker comes next.'>
        Linked Protocols (JSON)
      </FieldLabel>
      <textarea
        value={JSON.stringify(linked, null, 2)}
        onChange={(e) => {
          try { updateField(["linked_protocols"], JSON.parse(e.target.value || "[]")); } catch {}
        }}
        readOnly={readOnly}
        spellCheck="false"
        rows={Math.min(20, Math.max(6, linked.length * 4 + 4))}
        style={{
          width: "100%", padding: 10,
          fontFamily: "var(--font-mono)", fontSize: 12, lineHeight: 1.55,
          border: "1px solid #ccc", borderRadius: 4,
          background: readOnly ? "#f6f7f9" : "#fafafa",
        }}
      />
    </div>
  );
}

// ─── Tabs ───────────────────────────────────────────────────────────

function ConfigurationTab({ definition, updateField, readOnly, agentId }) {
  return (
    <div className="card">
      <div className="card-header">
        <h3>Configuration</h3>
      </div>
      <div className="form-field">
        <FieldLabel>ID</FieldLabel>
        <TextInput value={definition.id || agentId} readOnly />
      </div>
      <div className="form-field">
        <FieldLabel>Name</FieldLabel>
        <TextInput value={definition.name} onChange={(v) => updateField(["name"], v)} readOnly={readOnly} />
      </div>
      <div className="form-field">
        <FieldLabel hint="One of: framework, task, outbound. Determines how the orchestrator treats this agent.">Role</FieldLabel>
        <TextInput value={definition.role} onChange={(v) => updateField(["role"], v)} readOnly={readOnly} />
      </div>
      <div className="form-field">
        <FieldLabel>Entry — Direction</FieldLabel>
        <ChipsInput
          values={definition.entry?.direction}
          onChange={(v) => updateField(["entry", "direction"], v)}
          readOnly={readOnly}
          placeholder='inbound or outbound'
        />
      </div>
      <div className="form-field">
        <FieldLabel hint="Context fields that must be set before this agent can run (e.g. patientId).">Entry — Required Context</FieldLabel>
        <ChipsInput
          values={definition.entry?.requires_context}
          onChange={(v) => updateField(["entry", "requires_context"], v)}
          readOnly={readOnly}
          placeholder='e.g. patientId'
        />
      </div>
      <div className="form-field">
        <FieldLabel hint="Agent ids that typically run before this one. Informational; helps the orchestrator.">Typically preceded by</FieldLabel>
        <ChipsInput
          values={definition.entry?.typically_preceded_by}
          onChange={(v) => updateField(["entry", "typically_preceded_by"], v)}
          readOnly={readOnly}
          placeholder='e.g. verifier'
        />
      </div>
    </div>
  );
}

function PersonaTab({ definition, updateField, readOnly }) {
  const persona = definition.persona || {};
  return (
    <div className="card">
      <div className="card-header">
        <h3>Persona</h3>
        <span className="muted small" style={{ marginLeft: 8 }}>Who you are. What your job is. Stay-in-role guardrail.</span>
      </div>
      <div className="form-field">
        <FieldLabel>Role</FieldLabel>
        <TextInput value={persona.role} onChange={(v) => updateField(["persona", "role"], v)} readOnly={readOnly} placeholder='e.g. "the greeter"' />
      </div>
      <div className="form-field">
        <FieldLabel>Job</FieldLabel>
        <TextInput value={persona.job} onChange={(v) => updateField(["persona", "job"], v)} readOnly={readOnly} multiline rows={3} />
      </div>
      <div className="form-field">
        <FieldLabel>Guardrail</FieldLabel>
        <TextInput value={persona.guardrail} onChange={(v) => updateField(["persona", "guardrail"], v)} readOnly={readOnly} multiline rows={4} />
      </div>
    </div>
  );
}

function RulesTab({ definition, updateField, readOnly }) {
  const rules = definition.rules || [];
  const update = (idx, field, value) => {
    const next = rules.map((r, i) => (i === idx ? { ...r, [field]: value } : r));
    updateField(["rules"], next);
  };
  const add = () => updateField(["rules"], [...rules, { id: `R-${rules.length + 1}`, severity: "standard", text: "" }]);
  const remove = (idx) => updateField(["rules"], rules.filter((_, i) => i !== idx));

  return (
    <div className="card">
      <div className="card-header">
        <h3>Rules ({rules.length})</h3>
        {!readOnly && (
          <button className="btn sm" onClick={add} style={{ marginLeft: "auto" }}>+ Add Rule</button>
        )}
      </div>
      {rules.length === 0 && <div className="muted small">(no rules)</div>}
      {rules.map((r, idx) => (
        <div key={idx} style={{ border: "1px solid #e0e3e8", borderRadius: 6, padding: 10, marginBottom: 8 }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
            <div style={{ width: 100 }}>
              <FieldLabel>ID</FieldLabel>
              <TextInput value={r.id} onChange={(v) => update(idx, "id", v)} readOnly={readOnly} />
            </div>
            <div style={{ width: 130 }}>
              <FieldLabel>Severity</FieldLabel>
              <select
                value={r.severity || "standard"}
                onChange={(e) => update(idx, "severity", e.target.value)}
                disabled={readOnly}
                style={{ width: "100%", padding: "6px 8px", fontSize: 13, border: "1px solid #ccc", borderRadius: 4, background: readOnly ? "#f6f7f9" : "#fff" }}
              >
                <option value="critical">critical</option>
                <option value="standard">standard</option>
                <option value="hint">hint</option>
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <FieldLabel>Text</FieldLabel>
              <TextInput value={r.text} onChange={(v) => update(idx, "text", v)} readOnly={readOnly} multiline rows={2} />
            </div>
            {!readOnly && (
              <div style={{ display: "flex", alignItems: "flex-end" }}>
                <button className="btn sm" onClick={() => remove(idx)} style={{ color: "#c62828" }}>Delete</button>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function StepsTab({ definition, updateField, readOnly }) {
  const steps = definition.steps || [];
  const update = (idx, field, value) => {
    const next = steps.map((s, i) => (i === idx ? { ...s, [field]: value } : s));
    updateField(["steps"], next);
  };
  const add = () => updateField(["steps"], [...steps, { id: `S${steps.length + 1}`, name: "", body: "", waits_for_patient: false }]);
  const remove = (idx) => updateField(["steps"], steps.filter((_, i) => i !== idx));

  return (
    <div className="card">
      <div className="card-header">
        <h3>Steps ({steps.length})</h3>
        {!readOnly && (
          <button className="btn sm" onClick={add} style={{ marginLeft: "auto" }}>+ Add Step</button>
        )}
      </div>
      {steps.length === 0 && <div className="muted small">(no steps)</div>}
      {steps.map((s, idx) => (
        <div key={idx} style={{ border: "1px solid #e0e3e8", borderRadius: 6, padding: 10, marginBottom: 8 }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
            <div style={{ width: 100 }}>
              <FieldLabel>ID</FieldLabel>
              <TextInput value={s.id} onChange={(v) => update(idx, "id", v)} readOnly={readOnly} />
            </div>
            <div style={{ flex: 1 }}>
              <FieldLabel>Name</FieldLabel>
              <TextInput value={s.name} onChange={(v) => update(idx, "name", v)} readOnly={readOnly} />
            </div>
            <div style={{ width: 150, alignSelf: "flex-end" }}>
              <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}>
                <input
                  type="checkbox"
                  checked={!!s.waits_for_patient}
                  onChange={(e) => update(idx, "waits_for_patient", e.target.checked)}
                  disabled={readOnly}
                />
                Waits for patient
              </label>
            </div>
            {!readOnly && (
              <div style={{ alignSelf: "flex-end" }}>
                <button className="btn sm" onClick={() => remove(idx)} style={{ color: "#c62828" }}>Delete</button>
              </div>
            )}
          </div>
          <div className="form-field" style={{ marginTop: 4 }}>
            <FieldLabel>Body</FieldLabel>
            <TextInput value={s.body} onChange={(v) => update(idx, "body", v)} readOnly={readOnly} multiline rows={5} />
          </div>
        </div>
      ))}
    </div>
  );
}

function ExitTab({ definition, updateField, readOnly }) {
  const exit = definition.exit || {};
  return (
    <div className="card">
      <div className="card-header">
        <h3>Exit</h3>
        <span className="muted small" style={{ marginLeft: 8 }}>How this agent finishes and what it tells the orchestrator.</span>
      </div>
      <div className="form-field">
        <FieldLabel>Tool</FieldLabel>
        <TextInput value={exit.tool} onChange={(v) => updateField(["exit", "tool"], v)} readOnly={readOnly} placeholder='e.g. complete_task' />
      </div>
      <div className="form-field">
        <FieldLabel hint='What state must be true at exit? Example: { verified: true }'>Sets State (JSON)</FieldLabel>
        <TextInput
          value={exit.sets_state ? JSON.stringify(exit.sets_state, null, 2) : ""}
          onChange={(v) => {
            try { updateField(["exit", "sets_state"], JSON.parse(v || "{}")); } catch {}
          }}
          readOnly={readOnly}
          multiline
          rows={3}
        />
      </div>
      <div className="form-field">
        <FieldLabel>Notes</FieldLabel>
        <TextInput value={exit.notes} onChange={(v) => updateField(["exit", "notes"], v)} readOnly={readOnly} multiline rows={3} />
      </div>
    </div>
  );
}

function ToolsTab({ definition, updateField, readOnly }) {
  return (
    <div className="card">
      <div className="card-header">
        <h3>Tools ({(definition.tools || []).length})</h3>
        <span className="muted small" style={{ marginLeft: 8 }}>The LLM sees ONLY these tools when this agent is active.</span>
      </div>
      <div className="form-field">
        <FieldLabel>Tool IDs</FieldLabel>
        <ChipsInput
          values={definition.tools}
          onChange={(v) => updateField(["tools"], v)}
          readOnly={readOnly}
          placeholder='e.g. detect_intent'
        />
      </div>
      <div className="form-field" style={{ marginTop: 16 }}>
        <FieldLabel hint="Edit tool gates as JSON. A gate looks like { tool: 'schedule_appointment', require: ['patientId'], mode: 'enforce' }.">
          Tool Gates ({(definition.tool_gates || []).length})
        </FieldLabel>
        <TextInput
          value={JSON.stringify(definition.tool_gates || [], null, 2)}
          onChange={(v) => {
            try { updateField(["tool_gates"], JSON.parse(v || "[]")); } catch {}
          }}
          readOnly={readOnly}
          multiline
          rows={Math.min(15, Math.max(4, (definition.tool_gates || []).length * 4))}
        />
      </div>
    </div>
  );
}
